/**
 * AccessoryCard Component
 *
 * Displays an accessory in a compact card format for cross-selling on product detail pages.
 */

import { useState } from 'react';
import { CustomerAccessoryResponse, CustomerLocationDto } from '@/types';
import { Button } from '@/components/ui/button';
import { Plus, Check } from 'lucide-react';
import { useCartStore } from '@/stores/useCartStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { useToastStore } from '@/stores/useToastStore';
import { getRentalDayCount } from '@/lib/utils';
import { getLowestAccessoryTierPrice, hasAccessoryTieredPricing, getAccessoryTierForDays } from '@/lib/pricing';
import { PricingOptionType } from '@/types/cart';
import { LocationRequiredModal } from './LocationRequiredModal';
import { useFormatPrice } from '@/hooks/useFormatPrice';

interface AccessoryCardProps {
    accessory: CustomerAccessoryResponse;
    onAddToCart?: (accessory: CustomerAccessoryResponse) => void;
}

export const AccessoryCard = ({ accessory, onAddToCart }: AccessoryCardProps) => {
    const addItem = useCartStore((state) => state.addItem);
    const { dateRange, location, setLocation } = useLocationStore();
    const { success, warning } = useToastStore();
    const [showLocationModal, setShowLocationModal] = useState(false);
    const formatPrice = useFormatPrice();

    // Calculate rental days if dates are selected
    const rentalDays = dateRange.from && dateRange.to
        ? getRentalDayCount(dateRange.from, dateRange.to)
        : null;

    // Get pricing info
    const hasPricing = hasAccessoryTieredPricing(accessory);
    const lowestPrice = getLowestAccessoryTierPrice(accessory);
    const calculatedTier = rentalDays ? getAccessoryTierForDays(rentalDays, accessory) : null;

    const handleAddToCart = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Enforce location selection FIRST
        if (!location) {
            setShowLocationModal(true);
            return;
        }

        if (!dateRange.from || !dateRange.to) {
            warning("Please select rental dates first.");
            return;
        }

        // Add accessory to cart
            addItem({
                equipmentId: accessory.id,
                equipmentTypeId: accessory.id,
                quantity: 1,
                startDate: dateRange.from.toISOString(),
                endDate: dateRange.to.toISOString(),
                endDateIsExact: true,
                unitPrice: accessory.defaultPrice,
                locationId: location.id,
                productName: accessory.name,
                productImage: accessory.imageUrl,
                isAccessory: true,
                isSubscription: false,
                pricingOptionType: PricingOptionType.Rental,
                pricingOptionLabel: 'Accessory Rental',
            });

        if (onAddToCart) {
            onAddToCart(accessory);
        }

        success(`${accessory.name} added to cart!`);
    };

    const handleLocationSelected = (selectedLocation: CustomerLocationDto) => {
        setLocation(selectedLocation);
        // After setting location, try adding to cart again
        if (dateRange.from && dateRange.to) {
        addItem({
            equipmentId: accessory.id,
            equipmentTypeId: accessory.id,
            quantity: 1,
            startDate: dateRange.from.toISOString(),
            endDate: dateRange.to.toISOString(),
            endDateIsExact: true,
            unitPrice: accessory.defaultPrice,
            locationId: selectedLocation.id,
            productName: accessory.name,
            productImage: accessory.imageUrl,
            isAccessory: true,
            isSubscription: false,
            pricingOptionType: PricingOptionType.Rental,
            pricingOptionLabel: 'Accessory Rental',
        });
            success(`${accessory.name} added to cart!`);
        } else {
            warning("Please select rental dates to add this accessory.");
        }
    };

    // Render pricing
    const renderPrice = () => {
        // Bundled accessories show "Included" instead of a price
        if (accessory.isBundled) {
            return (
                <div className="text-right">
                    <span className="text-sm font-semibold text-green-600">
                        Included
                    </span>
                    <span className="text-xs text-muted-foreground block">
                        with rental
                    </span>
                </div>
            );
        }

        // If dates are selected and we have a calculated price
        if (rentalDays && calculatedTier) {
            return (
                <div className="text-right">
                        <span className="text-sm font-semibold text-primary">
                        {formatPrice(calculatedTier.price)}
                    </span>
                    <span className="text-xs text-muted-foreground block">
                        {calculatedTier.tierName}
                    </span>
                </div>
            );
        }

        // If tiered pricing is available, show "From €X"
        if (hasPricing && lowestPrice) {
            return (
                <div className="text-right">
                    <span className="text-xs text-muted-foreground">From </span>
                    <span className="text-sm font-semibold text-primary">
                        {formatPrice(lowestPrice)}
                    </span>
                </div>
            );
        }

        // Fallback to daily rate
        return (
            <div className="text-right">
                <span className="text-sm font-semibold text-primary">
                    {formatPrice(accessory.defaultPrice)}
                </span>
                <span className="text-xs text-muted-foreground">/day</span>
            </div>
        );
    };

    return (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-white hover:border-primary/50 hover:shadow-sm transition-all">
            {/* Image placeholder */}
            <div className="w-16 h-16 rounded-md bg-muted flex-shrink-0 flex items-center justify-center overflow-hidden">
                {accessory.imageUrl ? (
                    <img
                        src={accessory.imageUrl}
                        alt={accessory.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <span className="text-xs text-muted-foreground/70 text-center px-1">
                        {accessory.shortName || 'ACC'}
                    </span>
                )}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-foreground truncate">
                    {accessory.name}
                </h4>
                {accessory.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {accessory.description}
                    </p>
                )}
                {renderPrice()}
            </div>

            {/* Add button - or checkmark for bundled */}
            {accessory.isBundled ? (
                <div
                    className="flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md bg-green-100 text-green-600"
                    aria-label={`${accessory.name} is included with your rental`}
                >
                    <Check className="h-5 w-5" />
                </div>
            ) : (
                <Button
                    size="sm"
                    variant="outline"
                    className="flex-shrink-0 min-h-[44px] min-w-[44px] p-0"
                    onClick={handleAddToCart}
                    disabled={!accessory.isAvailable}
                    aria-label={`Add ${accessory.name} to cart`}
                >
                    <Plus className="h-4 w-4" />
                </Button>
            )}

            {/* Location Required Modal */}
            <LocationRequiredModal
                isOpen={showLocationModal}
                onClose={() => setShowLocationModal(false)}
                onLocationSelected={handleLocationSelected}
                productName={accessory.name}
                equipmentTypeId={accessory.id}
                startDate={dateRange.from?.toISOString()}
                endDate={dateRange.to?.toISOString()}
            />
        </div>
    );
};

export default AccessoryCard;
